// Configuration des devises supportées
export const SUPPORTED_CURRENCIES = {
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    nameTranslations: {
      fr: 'Euro',
      en: 'Euro',
      ar: 'يورو'
    },
    position: 'after', // 'before' ou 'after' le montant
    decimalPlaces: 2,
    countries: ['FR', 'DE', 'ES', 'IT', 'PT', 'AT', 'BE', 'NL', 'LU', 'IE', 'FI', 'GR', 'CY', 'MT', 'SI', 'SK', 'EE', 'LV', 'LT']
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    nameTranslations: {
      fr: 'Dollar américain',
      en: 'US Dollar',
      ar: 'دولار أمريكي'
    },
    position: 'before',
    decimalPlaces: 2,
    countries: ['US']
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    nameTranslations: {
      fr: 'Livre sterling',
      en: 'British Pound',
      ar: 'جنيه إسترليني'
    },
    position: 'before',
    decimalPlaces: 2,
    countries: ['GB']
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    nameTranslations: {
      fr: 'Dollar canadien',
      en: 'Canadian Dollar',
      ar: 'دولار كندي'
    },
    position: 'before',
    decimalPlaces: 2,
    countries: ['CA']
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    nameTranslations: {
      fr: 'Franc suisse',
      en: 'Swiss Franc',
      ar: 'فرنك سويسري'
    },
    position: 'after',
    decimalPlaces: 2,
    countries: ['CH']
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    nameTranslations: {
      fr: 'Yen japonais',
      en: 'Japanese Yen',
      ar: 'ين ياباني'
    },
    position: 'before',
    decimalPlaces: 0,
    countries: ['JP']
  },
  DZD: {
    code: 'DZD',
    symbol: 'DA',
    name: 'Algerian Dinar',
    nameTranslations: {
      fr: 'Dinar algérien',
      en: 'Algerian Dinar',
      ar: 'دينار جزائري'
    },
    position: 'after',
    decimalPlaces: 2,
    countries: ['DZ']
  },
  MAD: {
    code: 'MAD',
    symbol: 'DH',
    name: 'Moroccan Dirham',
    nameTranslations: {
      fr: 'Dirham marocain',
      en: 'Moroccan Dirham',
      ar: 'درهم مغربي'
    },
    position: 'after',
    decimalPlaces: 2,
    countries: ['MA']
  },
  TND: {
    code: 'TND',
    symbol: 'TND',
    name: 'Tunisian Dinar',
    nameTranslations: {
      fr: 'Dinar tunisien',
      en: 'Tunisian Dinar',
      ar: 'دينار تونسي'
    },
    position: 'after',
    decimalPlaces: 3,
    countries: ['TN']
  }
};

// Devise par défaut
export const DEFAULT_CURRENCY = 'EUR';

// Fonction pour obtenir la devise selon le pays
export const getCurrencyByCountry = (countryCode) => {
  if (!countryCode) return DEFAULT_CURRENCY;
  
  for (const [currencyCode, currency] of Object.entries(SUPPORTED_CURRENCIES)) {
    if (currency.countries.includes(countryCode.toUpperCase())) {
      return currencyCode;
    }
  }
  
  return DEFAULT_CURRENCY;
};

// Fonction pour obtenir la devise selon la locale du navigateur
export const getCurrencyByLocale = () => {
  try {
    // Essayer de détecter la locale du navigateur
    const locale = navigator.language || navigator.languages[0];
    const countryCode = locale.split('-')[1];
    
    if (countryCode) {
      return getCurrencyByCountry(countryCode);
    }
    
    // Fallback selon la langue
    const languageCode = locale.split('-')[0].toLowerCase();
    switch (languageCode) {
      case 'fr':
        return 'EUR';
      case 'en':
        return 'USD';
      case 'ar':
        return 'DZD'; // Par défaut pour l'arabe
      default:
        return DEFAULT_CURRENCY;
    }
  } catch (error) {
    console.warn('Error detecting locale for currency:', error);
    return DEFAULT_CURRENCY;
  }
};

// Fonction pour formater un prix selon la devise
export const formatPrice = (amount, currencyCode = DEFAULT_CURRENCY, locale = 'fr-FR') => {
  if (!amount || amount === '') return 'N/A';
  
  const currency = SUPPORTED_CURRENCIES[currencyCode];
  if (!currency) return `${amount}`;
  
  try {
    // Utiliser l'API Intl.NumberFormat pour un formatage correct selon la locale
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: currency.decimalPlaces,
      maximumFractionDigits: currency.decimalPlaces
    });
    
    return formatter.format(Number(amount));
  } catch (error) {
    console.warn('Error formatting price:', error);
    // Fallback manuel
    const formattedAmount = Number(amount).toFixed(currency.decimalPlaces);
    return currency.position === 'before' 
      ? `${currency.symbol}${formattedAmount}`
      : `${formattedAmount} ${currency.symbol}`;
  }
};

// Fonction pour obtenir la liste des devises pour un sélecteur
export const getCurrencyOptions = (language = 'fr') => {
  return Object.values(SUPPORTED_CURRENCIES).map(currency => ({
    value: currency.code,
    label: `${currency.nameTranslations[language] || currency.name} (${currency.symbol})`,
    symbol: currency.symbol
  }));
};
